import { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
} from "@chakra-ui/react";
import { Plus, Minus } from "~/icons";
import { ArticleRow } from "../../../../LandingApp/pages/LibraryPage/components/ArticleRow";

// Define the featured articles to display
const FEATURED_ARTICLES = [
  { slug: "the-conceive-basics", topic: "fertility-101" },
  { slug: "tl-dr-guide-to-fertility-testing", topic: "fertility-diagnoses" },
  { slug: "creating-your-conceive-un-plan", topic: "fertility-101" },
];

export const Resources: FC = () => {
  const { t } = useTranslation();

  return (
    <Box bg="#fcf9f1" w="100%" py="60px" pb="80px">
      <VStack spacing="40px" align="center">
        {/* Support Section */}
        <Box
          w="100%"
          maxW="900px"
          bg="#FFCDD9"
          borderRadius="20px"
          py="50px"
          px={{ base: "30px", md: "60px" }}
          mb="60px"
          position="relative"
          overflow="hidden"
        >
          {/* Wave Pattern - Absolute positioned on left */}
          <Box
            position="absolute"
            left={{ base: "-50px", md: "40px" }}
            top="50%"
            transform="translateY(-50%)"
            w={{ base: "150px", md: "200px" }}
            h={{ base: "150px", md: "200px" }}
          >
            {[1, 2, 3, 4, 5].map((ring) => (
              <Box
                key={ring}
                position="absolute"
                border="3px solid"
                borderColor={`rgba(255, 182, 193, ${0.5 - ring * 0.1})`}
                borderRadius="50%"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                w={`${ring * 40}px`}
                h={`${ring * 40}px`}
              />
            ))}
          </Box>

          {/* Content - Centered */}
          <VStack
            spacing="20px"
            align="center"
            textAlign="center"
            position="relative"
            zIndex={1}
          >
            <Box
              as="span"
              bg="white"
              px="6px"
              py="2px"
              borderRadius="4px"
              display="inline-block"
            >
              <Heading
                fontSize="28px"
                fontWeight="700"
                lineHeight="1.2"
                color="#111a18"
              >
                {t("diagnosticDashboard.support.title")}
              </Heading>
            </Box>
            <Text
              fontSize="16px"
              fontWeight="400"
              lineHeight="1.5"
              color="#111a18"
              maxW="520px"
            >
              {t("diagnosticDashboard.support.description")}
            </Text>
            <Box
              as="a"
              href="https://weconceive.com/onboarding"
              bg="white"
              color="#111a18"
              px="28px"
              py="10px"
              borderRadius="8px"
              fontSize="16px"
              fontWeight="600"
              mt="8px"
              _hover={{ bg: "gray.50" }}
              transition="background 0.2s"
              display="inline-block"
              textDecoration="none"
            >
              {t("diagnosticDashboard.support.cta")}
            </Box>
          </VStack>
        </Box>
        <VStack spacing="20px" maxW="749px" textAlign="center">
          <Heading as="h2" size="xl" textAlign="center" >
            {t("diagnosticDashboard.resources.title")}
          </Heading>
          <Text
            fontSize="16px"
            fontWeight="400"
            lineHeight="1.4"
            color="#111a18"
          >
            {t("diagnosticDashboard.resources.description")}
          </Text>
        </VStack>
        <HStack spacing="16px" justify="center">
          <ArticleRow articles={FEATURED_ARTICLES} />
        </HStack>

        {/* FAQ Section */}
        <VStack spacing={0} w="100%" maxW="800px" mt="60px">
          <Heading as="h2" size="xl" textAlign="center" mb="40px" >
            {t("diagnosticDashboard.faq.heading")}
          </Heading>

          {/* FAQ Items */}
          <Accordion allowToggle w="100%">
            {t("diagnosticDashboard.faq.items", { returnObjects: true }).map(
              (item: any, index: number) => (
                <AccordionItem
                  key={index}
                  border="none"
                  borderTop={index > 0 ? "1px solid #E5E5E5" : "none"}
                >
                  {({ isExpanded }) => (
                    <>
                      <AccordionButton
                        _hover={{ bg: "rgba(0,0,0,0.02)" }}
                        py="24px"
                        px="16px"
                      >
                        <Box flex="1" textAlign="left">
                          <Text
                            fontSize="16px"
                            fontWeight="400"
                            lineHeight="1.4"
                            color="#111a18"
                          >
                            {item.question}
                          </Text>
                        </Box>
                        {isExpanded ? (
                          <Minus width="20px" height="20px" color="#111a18" />
                        ) : (
                          <Plus width="20px" height="20px" color="#111a18" />
                        )}
                      </AccordionButton>
                      <AccordionPanel pb={4} px="16px">
                        <Text fontSize="14px" color="#666">
                          {item.answer}
                        </Text>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>
              ),
            )}
          </Accordion>
        </VStack>

      </VStack>
    </Box>
  );
};
